
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import Chart from 'chart.js/auto';
import { ChartConfiguration } from 'chart.js';
import { DateTime } from 'luxon';

import { sharedState } from '../../../../framework/state';
import { getResourcesNew } from '../../../../framework/client/resource';

import {
    barChartOptions,
    chartBackgroundColors,
    getMonthLabelsNew,
    getTitleOptions,
    numberTooltipCallbacks,
} from '../../../service/report_service';
import { VehicleUsage } from '../../../../../common/application/model/VehicleUsage';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';

import { formatNumber } from '../../../service/helper_service';
import { formatMonthStrings } from '../../../../../common/framework/util/time';
import { dateToDbMonthDateString, getLocalDate } from '../../../../../common/framework/util/convert';
import { P_PAGE_SIZE } from '../../../../../common/framework/constants';

@Component
export default class VehicleMonthlyConsumptionChart extends Vue {
    readonly resourceType = ApplicationResource.VEHICLE_USAGE;

    @Prop(String) readonly vehicleContractId!: string;
    @Prop(Array) readonly customerIds!: string[];
    @Prop(Date) readonly lastMonth!: Date;
    @Prop(Array) readonly vehicleUsageData!: VehicleUsage[];

    shared = sharedState;

    local = {
        vehicleMonthlyConsumptionChart: undefined as Chart | undefined,
        chartData: [] as VehicleUsage[],
        fakeChartData: [] as any[],
        months: [] as string[],
    };

    async mounted() {
        await this.loadAsyncData();
        await this.addVehicleMonthlyConsumptionChart();
    }

    @Watch('customerIds')
    async customerIdsChanged() {
        await this.loadAsyncData();
        await this.addVehicleMonthlyConsumptionChart();
    }

    @Watch('lastMonth')
    async lastMonthChanged() {
        await this.loadAsyncData();
        await this.addVehicleMonthlyConsumptionChart();
    }

    @Watch('vehicleUsageData')
    async vehicleUsageDataChanged() {
        await this.loadAsyncData();
        await this.addVehicleMonthlyConsumptionChart();
    }

    private async loadAsyncData() {
        const beginDate = DateTime.fromJSDate(this.lastMonth || new Date())
            .minus({ months: 11 })
            .toJSDate();
        this.local.months = formatMonthStrings(beginDate.getFullYear(), beginDate.getMonth(), 11);

        if (this.vehicleUsageData) {
            this.local.chartData = [...this.vehicleUsageData];
        } else if (this.vehicleContractId) {
            const parameters = new Map<string, string>([
                ['vehicleContractId', this.vehicleContractId],
                ['page', '1'],
                ['sortField', 'vehicleUsageDate'],
                ['sortOrder', 'desc'],
                [P_PAGE_SIZE, '12'],
            ]);

            this.local.chartData = await getResourcesNew<VehicleUsage>(
                this.resourceType,
                parseInt(parameters.get('page') || '1'),
                parameters,
            );
        }

        if (this.local.chartData.length === 0) {
            this.generateFakeData();
        }
    }

    private async addVehicleMonthlyConsumptionChart() {
        const canvasContext = (document.getElementById(
            'vehicle-monthly-consumption-chart',
        ) as HTMLCanvasElement)!!.getContext('2d')!!;

        const chartConfiguration: ChartConfiguration = {
            type: 'bar',
            data: {
                labels: getMonthLabelsNew(this, this.local.months),
                datasets: this.chartDataSets,
            },
            options: {
                ...barChartOptions,
                plugins: {
                    ...barChartOptions.plugins,
                    title: getTitleOptions(`${this.$t(this.titleString)} [l/100km]`),
                    tooltip: {
                        callbacks: { ...numberTooltipCallbacks },
                    },
                },
                scales: {
                    x: {
                        stacked: true,
                    },
                    y: {
                        stacked: true,
                        ticks: {
                            callback: (value) => formatNumber(value as number),
                        },
                    },
                },
            },
        };

        this.local.vehicleMonthlyConsumptionChart?.destroy();
        this.local.vehicleMonthlyConsumptionChart = new Chart(canvasContext, chartConfiguration);
    }

    private get titleString(): string {
        if (this.vehicleContractId) {
            return 'title.vehicleMonthlyConsumptionChart';
        } else {
            return 'title.vehiclesMonthlyConsumptionChart';
        }
    }

    private static get backgroundColors(): string[] {
        return chartBackgroundColors;
    }

    private get chartDataSets(): any[] {
        const dataSets: any[] = [
            {
                label: `${this.$t('label.monthlyValue')}`,
                data: [],
                backgroundColor: VehicleMonthlyConsumptionChart.backgroundColors[1],
            },
            {
                label: `${this.$t('label.cumulativeValue')}`,
                data: [],
                type: 'line',
                borderColor: VehicleMonthlyConsumptionChart.backgroundColors[3],
                backgroundColor: VehicleMonthlyConsumptionChart.backgroundColors[3],
                fill: false,
            },
        ];

        let cumulativeValue = 0;
        let preCumulativeValue = 0;

        this.local.months.forEach((month, index) => {
            let chartDataToUse: VehicleUsage[] = [];

            if (this.local.chartData.length) {
                chartDataToUse = [...this.local.chartData];
            } else {
                chartDataToUse = [...this.local.fakeChartData];
            }

            const consumptionArr = chartDataToUse
                .filter(
                    (vehicleUsage) =>
                        dateToDbMonthDateString(getLocalDate(new Date(vehicleUsage.vehicleUsageDate))) === month,
                )
                .map((vehicleUsage) => vehicleUsage.consumption);

            const totalConsumption = consumptionArr.length
                ? consumptionArr.reduce((a, b) => (a || 0) + (b || 0), 0)
                : 0;

            if (dataSets[0] && dataSets[0].data) {
                dataSets[0].data.push(totalConsumption ? totalConsumption : 0);
            }

            const previousTotalConsumption = chartDataToUse
                .filter(
                    (vehicleUsage) =>
                        dateToDbMonthDateString(getLocalDate(new Date(vehicleUsage.vehicleUsageDate))) ===
                        this.local.months[index - 1],
                )
                .map((vehicleUsage) => vehicleUsage.consumption)
                .reduce((a, b) => (a || 0) + (b || 0), 0);

            if (dataSets[1] && dataSets[1].data) {
                cumulativeValue += totalConsumption ? totalConsumption : 0;
                preCumulativeValue += previousTotalConsumption ? previousTotalConsumption : 0;
                dataSets[1].data.push(totalConsumption ? cumulativeValue : preCumulativeValue);
            }
        });

        return dataSets;
    }

    private generateFakeData(): void {
        this.local.fakeChartData = [];

        this.local.months.forEach((month) => {
            this.local.fakeChartData.push({
                vehicleUsageDate: DateTime.fromFormat(month, 'yyyy-MM').toJSDate(),
                consumption: Math.floor(Math.random() * (20 - 2 + 1) + 2),
            });
        });
    }
}
